import React from "react";
import { Course, DaySchedule, asyncGetSchedule } from "lakshya-shared";
import DefaultModule from "..";

type StudyPlanProps = {
  course: Course;
  module: DefaultModule;
};

type StudyPlanNote = {
  id: string;
  afterDayIndex: number; // -1 means before Day 0
  title?: string;
  content: string;
  visible?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export default function StudyPlan(props: StudyPlanProps) {
  const { course } = props;
  const [schedule, setSchedule] = React.useState<DaySchedule[]>([]);
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(0);
  const [notes, setNotes] = React.useState<StudyPlanNote[]>(
    (((course as any).studyPlanNotes as StudyPlanNote[]) || [])
  );
  const [activeFormAfterDayIndex, setActiveFormAfterDayIndex] = React.useState<number | null>(null);
  const [draftTitle, setDraftTitle] = React.useState<string>("");
  const [draftContent, setDraftContent] = React.useState<string>("");

  React.useEffect(() => {
    if (!course) return;
    let isMounted = true;
    const useCache = Array.isArray(course.subjects)
      ? course.subjects.some(
          (s: any) =>
            Array.isArray(s.lecturesMetaData) && s.lecturesMetaData.length > 0
        )
      : false;
    try {
      const value = asyncGetSchedule(course, useCache);
      if (isMounted && Array.isArray(value)) setSchedule(value);
    } catch (e) {
      // ignore schedule build errors
    }
    return () => {
      isMounted = false;
    };
  }, [course]);

  React.useEffect(() => {
    setNotes((((course as any).studyPlanNotes as StudyPlanNote[]) || []));
  }, [course]);

  if (!Array.isArray(schedule)) {
    return null;
  }

  const toggleIndex = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const handleSaveNotes = (updated: StudyPlanNote[]) => {
    setNotes(updated);
    props.module.controller.updateCourse("studyPlanNotes", updated);
    props.module.controller.saveCourse();
  };

  const handleAddNote = (dayIndexOrSpecial: number) => {
    const id = `${Date.now()}-${Math.round(Math.random() * 1000000)}`;
    const storedAfterDayIndex = dayIndexOrSpecial === -1 ? -1 : dayIndexOrSpecial + 1;
    const newNote: StudyPlanNote = {
      id,
      afterDayIndex: storedAfterDayIndex,
      title: draftTitle ? draftTitle.trim() : undefined,
      content: draftContent.trim(),
      visible: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const updated = [...notes, newNote];
    handleSaveNotes(updated);
    setDraftTitle("");
    setDraftContent("");
    setActiveFormAfterDayIndex(null);
  };

  const handleDeleteNote = (id: string) => {
    const updated = notes.filter((n) => n.id !== id);
    handleSaveNotes(updated);
  };

  return (
    <div className="col-12 mt-3">
      {schedule.length < 1 ? (
        <div className="alert alert-warning my-3" role="alert">
          No study plan yet!
        </div>
      ) : null}

      {/* Intro notes (before Day 0) */}
      {(notes || []).filter((n) => (n.visible ?? true) && n.afterDayIndex === -1).length > 0 ? (
        <div className="mb-3">
          {(notes || [])
            .filter((n) => (n.visible ?? true) && n.afterDayIndex === -1)
            .map((n) => (
              <div className="card border-left-0 border-right-0 border-top-0 mb-2" key={n.id}>
                <div className="card-body py-3 px-4">
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      {n.title ? <div className="font-weight-bold mb-1">{n.title}</div> : null}
                      <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>{n.content}</div>
                    </div>
                    <button className="btn btn-sm btn-outline-danger ml-3" onClick={() => handleDeleteNote(n.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : null}

      <div className="mb-4">
        {activeFormAfterDayIndex === -1 ? (
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="form-group">
                <label>Title (optional)</label>
                <input className="form-control" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} />
              </div>
              <div className="form-group mb-2">
                <label>Content</label>
                <textarea className="form-control" rows={3} value={draftContent} onChange={(e) => setDraftContent(e.target.value)} />
              </div>
              <div className="d-flex">
                <button disabled={!draftContent.trim()} className="btn btn-primary mr-2" onClick={() => handleAddNote(-1)}>Add Note</button>
                <button className="btn btn-light" onClick={() => { setActiveFormAfterDayIndex(null); setDraftTitle(""); setDraftContent(""); }}>Cancel</button>
              </div>
            </div>
          </div>
        ) : (
          <button className="btn btn-outline-primary btn-sm" onClick={() => setActiveFormAfterDayIndex(-1)}>Add intro note</button>
        )}
      </div>

      <div className="accordion" id="study-plan-accordion">
        {schedule.map((day, index) => {
          const isOpen = expandedIndex === index;
          return (
            <div className="card mb-3 shadow-sm" key={day.dayIndex}>
              <div
                className="card-header d-flex justify-content-between align-items-center bg-white py-3 px-4"
                onClick={() => toggleIndex(index)}
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleIndex(index);
                  }
                }}
              >
                <div className="d-flex align-items-center">
                  <i
                    className={`fas fa-chevron-${isOpen ? "down" : "right"} mr-3 text-muted`}
                  ></i>
                  <h5 className="mb-0">{day.title}</h5>
                </div>
                <div className="d-flex align-items-center">
                  <span className="badge badge-secondary mr-2">
                    {day.lectures.length} Lectures
                  </span>
                  <span className="badge badge-info">
                    {day.exams.length} Exams
                  </span>
                </div>
              </div>
              {isOpen ? (
                <div className="collapse show">
                  <div className="card-body p-4">
                    <div className="row">
                      <div className="col-12 col-md-6 pr-md-3">
                        <div className="text-uppercase text-muted small mb-2">Lectures</div>
                        {day.lectures.length < 1 ? (
                          <div className="text-muted">No lectures</div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {day.lectures.map((lec, i) => (
                              <li className="list-group-item d-flex align-items-center" key={i}>
                                <i className="fas fa-video text-muted mr-3"></i>
                                <span>{lec.title}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="col-12 col-md-6 mt-4 mt-md-0 pl-md-3">
                        <div className="text-uppercase text-muted small mb-2">Exams</div>
                        {day.exams.length < 1 ? (
                          <div className="text-muted">No exams</div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {day.exams.map((ex, i) => (
                              <li className="list-group-item d-flex align-items-center" key={i}>
                                <i className="fas fa-file-alt text-muted mr-3"></i>
                                <span>{ex.title}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {(notes || []).filter((n) => (n.visible ?? true) && n.afterDayIndex === index + 1).length > 0 ? (
                      <div className="mt-4">
                        {(notes || [])
                          .filter((n) => (n.visible ?? true) && n.afterDayIndex === index + 1)
                          .map((n) => (
                            <div className="card border-left-0 border-right-0 border-top-0 mb-2" key={n.id}>
                              <div className="card-body py-3 px-4">
                                <div className="d-flex align-items-start justify-content-between">
                                  <div>
                                    {n.title ? <div className="font-weight-bold mb-1">{n.title}</div> : null}
                                    <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>{n.content}</div>
                                  </div>
                                  <button className="btn btn-sm btn-outline-danger ml-3" onClick={() => handleDeleteNote(n.id)}>Delete</button>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : null}

                    <div className="mt-3">
                      {activeFormAfterDayIndex === index ? (
                        <div className="card shadow-sm">
                          <div className="card-body">
                            <div className="form-group">
                              <label>Title (optional)</label>
                              <input className="form-control" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} />
                            </div>
                            <div className="form-group mb-2">
                              <label>Content</label>
                              <textarea className="form-control" rows={3} value={draftContent} onChange={(e) => setDraftContent(e.target.value)} />
                            </div>
                            <div className="d-flex">
                              <button disabled={!draftContent.trim()} className="btn btn-primary mr-2" onClick={() => handleAddNote(index)}>Add Note</button>
                              <button className="btn btn-light" onClick={() => { setActiveFormAfterDayIndex(null); setDraftTitle(""); setDraftContent(""); }}>Cancel</button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button className="btn btn-outline-primary btn-sm" onClick={() => setActiveFormAfterDayIndex(index)}>Add note after this day</button>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}


